import { Button, TextField, Typography } from '@mui/material'
import { Box } from '@mui/system'
import CustomBackdrop from 'common/components/CustomBackdrop'
import { selectPortal } from 'features/portal/portalSlice'
import Icons from 'icons/Icons'
import i18n from 'lang/idiomas'
import { SnackbarProvider, enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import PasswordController from '../lib/PasswordController'
import PasswordRequirements from './PasswordRequirements'

/**
 * A Form to change the user password
 * @param {Object} props The react object props
 * @param {string} [props.username=null] The username to send on the request, only uset to restablish the password
 * @param {'filled' | 'outlined' | 'standard'} [props.variant='outlined']
 * @param {string} [props.buttonWidth}
 * @param {boolean} [props.showRequirements=true]}
 */
const PasswordForm = ({
  token = null,
  buttonWidth = 'initial',
  variant = 'outlined',
  showRequirements = true,
}) => {
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('')
  const [currentPassword, setCurrentPassword] = useState(token ? null : '')
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [passwordChanged, setPasswordChanged] = useState(false)

  const portal = useSelector(selectPortal)
  const changePasswordRegex = useCallback(() => {
    return PasswordController.createQuerySet(
      portal?.portal?.longitud_password || 8,
      portal?.portal?.obligar_mayusculas === 'S',
      portal?.portal?.obligar_numero === 'S',
      portal?.portal?.obligar_simbolo === 'S',
    )
  }, [portal])
  const [PASSWORD_REGEX, setPasswordRegex] = useState(changePasswordRegex())

  useEffect(() => {
    setPasswordRegex(changePasswordRegex())
  }, [portal, changePasswordRegex])

  const changePassword = async () => {
    try {
      setOpenBackdrop(true)
      await PasswordController.changePassword(
        newPassword,
        newPasswordRepeat,
        currentPassword,
        token,
      )
      setOpenBackdrop(false)
      setPasswordChanged(true)
      enqueueSnackbar(
        i18n.t('cgpasok', {
          defaultValue:
            'Contraseña cambiada correctamente.\n\nPor favor, inicie sesión con su nueva contraseña.',
        }),
        {
          variant: 'success',
        },
      )
    } catch (error) {
      setOpenBackdrop(false)
      enqueueSnackbar(
        i18n.t(error?.response?.data?.msg_code, {
          defaultValue: error?.response?.data?.default_msg,
        }),
        { variant: 'error' },
      )
    }
  }

  return (
    <>
      <SnackbarProvider
        maxSnack={1}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant={'error'}
      />
      <CustomBackdrop
        open={openBackdrop}
        text={i18n.t('chgingpa', { defaultValue: 'Cambiando contraseña' })}
      />

      {passwordChanged ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Icons
            icon="fi-rr-check"
            sx={{ fontSize: 160, color: 'success.main', aspectRatio: 'unset', width: 'auto' }}
          />
          <Box>
            <Typography variant="h6" color="success" fontWeight={'bold'}>
              {i18n.t('paschgok', { defaultValue: 'Contraseña cambiada correctamente.' })}
            </Typography>
            <Typography variant="body1">
              {i18n.t('pfinises', {
                defaultValue: 'Por favor, inicie sesión con su nueva contraseña.',
              })}
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            component={'form'}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1em',
            }}
          >
            {showRequirements ? <PasswordRequirements /> : <></>}
            {token ? (
              <></>
            ) : (
              <TextField
                id="curront-password"
                value={currentPassword}
                type="password"
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={i18n.t('usuarios.uconactm', { defaultValue: 'Contraseña Actual' })}
                variant={variant}
              />
            )}
            <TextField
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={i18n.t('usuarios.unueconm', { defaultValue: 'Nueva Contraseña' })}
              type="password"
              error={newPassword && !PASSWORD_REGEX.test(newPassword)}
              variant={variant}
            />
            <TextField
              id="new-password-repeat"
              value={newPasswordRepeat}
              onChange={(e) => setNewPasswordRepeat(e.target.value)}
              type="password"
              placeholder={i18n.t('usuarios.urecontm', {
                defaultValue: 'Repetir Nueva Contraseña',
              })}
              error={newPasswordRepeat !== newPassword}
              variant={variant}
            />
          </Box>

          <Button
            sx={{ mt: '2em', width: buttonWidth }}
            variant="contained"
            size="large"
            disabled={
              (!token && !currentPassword) ||
              newPassword !== newPasswordRepeat ||
              !newPassword ||
              !PASSWORD_REGEX.test(newPassword)
            }
            onClick={changePassword}
            disableElevation
          >
            {i18n.t('usuarios.ucambcon', { defaultValue: 'Cambiar Contraseña' })}
          </Button>
        </>
      )}

      {token ? (
        <Button sx={{ width: '100%', mt: 2 }} LinkComponent={Link} to="/login">
          {i18n.t('login.login', { defaultValue: 'Iniciar Sesión' })}
        </Button>
      ) : (
        <></>
      )}
    </>
  )
}

export default PasswordForm
